import { useMemo } from "react";

import { DutyScheduleCounselorOptionResponse, DutyScheduleDetailResponse } from "@/api";
import { CheckboxMultiSelect } from "@/common/components/fields/checkbox-multi-select/CheckboxMultiSelect";
import { userCodesByCounselorId } from "@/pages/duty-schedule/pages/duty-schedule-detail/pages/data-entry/DutyScheduleDataEntry.utils";
import { useTranslation } from "@/translations";

const FILTER_MIN_WIDTH = 300;
const FILTER_MAX_WIDTH = 480;
// Beyond this many chips the rest collapse into a single "+N" indicator, always — not just while
// unfocused, unlike MUI's own `limitTags` — opening the dropdown to pick another name must not
// re-expand the row and grow the whole tab bar's height. The dropdown's checkmarks already show the
// full selection, so nothing is lost by keeping the chip row itself capped.
const MAX_VISIBLE_CHIPS = 2;

interface Props {
  dutySchedule: DutyScheduleDetailResponse;
  counselors: DutyScheduleCounselorOptionResponse[];
  // null means unfiltered — same as an empty selection, which CheckboxMultiSelect never actually
  // produces here because clearing the last chip already lands back on null via onChange below.
  value: string[] | null;
  onChange: (counselorIds: string[] | null) => void;
}

// The options are the counselors who actually hold a duty in this duty schedule, not every staff
// member: filtering to somebody who holds no duty here would only ever empty the table. Labels come
// from the current staff list, falling back to the bare code for somebody who has since left.
export function DutyScheduleCounselorFilter({ dutySchedule, counselors, value, onChange }: Props) {
  const { t } = useTranslation();

  const counselorsById = useMemo(() => new Map(counselors.map((counselor) => [counselor.id, counselor])), [counselors]);

  // Memoised: this walks every slot of the plan, and the filter re-renders on every cell click.
  const userCodesInPlan = useMemo(() => userCodesByCounselorId(dutySchedule.slots), [dutySchedule.slots]);
  const counselorIdsInPlan = useMemo(
    () =>
      [...userCodesInPlan.entries()]
        .sort(([, left], [, right]) => left.localeCompare(right))
        .map(([counselorId]) => counselorId),
    [userCodesInPlan]
  );

  function userCodeFor(counselorId: string): string {
    return userCodesInPlan.get(counselorId) ?? counselorsById.get(counselorId)?.userCode ?? "";
  }

  function labelFor(counselorId: string): string {
    const userCode = userCodeFor(counselorId);
    const counselor = counselorsById.get(counselorId);

    return counselor == null
      ? userCode
      : t("dutySchedule.detail.dataEntry.filter.option", {
          userCode,
          lastName: counselor.lastName,
          firstName: counselor.firstName,
        });
  }

  return (
    <CheckboxMultiSelect
      label={t("dutySchedule.detail.dataEntry.filter.label")}
      options={counselorIdsInPlan}
      value={value ?? []}
      onChange={(newValue) => onChange(newValue.length === 0 ? null : newValue)}
      getOptionLabel={labelFor}
      getOptionKey={(counselorId) => counselorId}
      getChipLabel={userCodeFor}
      maxVisibleChips={MAX_VISIBLE_CHIPS}
      sx={{ minWidth: FILTER_MIN_WIDTH, maxWidth: FILTER_MAX_WIDTH }}
    />
  );
}
